import { useState, useEffect, useCallback, useRef } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { getAllInteriorTypes, createMaterial, createProduct } from '../api/supplierInteriors';

/**
 * Upload Material / Upload Product Component
 * Shown at /upload-product after user clicks "Let's Begin" on Supplier.
 * Left: preview of uploaded image. Right: Name, Description, Interior Type (multi-select), Materials/Texture/Color; Add Product button.
 * Existing createMaterial flow kept for upgrade path.
 */

const UploadMaterial = () => {
  const { supplierUploadedImage, supplierUploadedFile, setCurrentView } = useSidebar();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [materialsTextureColor, setMaterialsTextureColor] = useState('');
  const [materialIds, setMaterialIds] = useState([]);
  const [materialTypeQuery, setMaterialTypeQuery] = useState('');
  const [materialTypeDropdownOpen, setMaterialTypeDropdownOpen] = useState(false);
  const [interiorTypesList, setInteriorTypesList] = useState([]);
  const [interiorTypesLoading, setInteriorTypesLoading] = useState(false);
  const [selectedInteriorTypes, setSelectedInteriorTypes] = useState([]); // [{ id, interiorType }, ...]
  const [addMaterialLoading, setAddMaterialLoading] = useState(false);
  const [addMaterialError, setAddMaterialError] = useState(null);
  const [addMaterialSuccess, setAddMaterialSuccess] = useState(false);

  const [showAddMaterialPopup, setShowAddMaterialPopup] = useState(false);
  const [addMaterialPopupFile, setAddMaterialPopupFile] = useState(null);
  const [addMaterialPopupType, setAddMaterialPopupType] = useState('');
  const [addMaterialPopupLoading, setAddMaterialPopupLoading] = useState(false);
  const [addMaterialPopupError, setAddMaterialPopupError] = useState(null);
  const addMaterialPopupInputRef = useRef(null);

  useEffect(() => {
    if (interiorTypesList.length > 0 || interiorTypesLoading) return;
    setInteriorTypesLoading(true);
    getAllInteriorTypes()
      .then((list) => setInteriorTypesList(Array.isArray(list) ? list : []))
      .catch((err) => console.error('Failed to load interior types:', err))
      .finally(() => setInteriorTypesLoading(false));
  }, [interiorTypesList.length, interiorTypesLoading]);

  const filteredTypes = interiorTypesList.filter((t) =>
    String(t.interiorType).toLowerCase().includes((materialTypeQuery || '').toLowerCase())
  );

  const addSelected = useCallback((item) => {
    if (!item) return;
    setSelectedInteriorTypes((prev) => {
      if (prev.some((s) => s.id === item.id)) return prev;
      return [...prev, { id: item.id, interiorType: item.interiorType }];
    });
    setMaterialTypeQuery('');
    setMaterialTypeDropdownOpen(false);
  }, []);

  const removeSelected = useCallback((id) => {
    setSelectedInteriorTypes((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const handleAddProduct = useCallback(async () => {
    const nameTrimmed = (name || '').trim();
    if (!nameTrimmed) {
      setAddMaterialError('Please enter a product name.');
      return;
    }
    if (selectedInteriorTypes.length === 0) {
      setAddMaterialError('Please select at least one interior type.');
      return;
    }
    if (!supplierUploadedFile) {
      setAddMaterialError('Please upload an image on the Supplier page first.');
      return;
    }
    setAddMaterialError(null);
    setAddMaterialSuccess(false);
    setAddMaterialLoading(true);
    try {
      const descriptionPayload = description.trim() ? JSON.stringify({ text: description.trim() }) : '';
      const selectedTypes = [...selectedInteriorTypes];
      const failures = [];
      let successCount = 0;

      // Backend currently expects one integer interior_type_id per request.
      for (const type of selectedTypes) {
        const res = await createProduct({
          name: nameTrimmed,
          description: descriptionPayload,
          interior_type_id: String(type.id),
          material_ids: materialIds.length > 0 ? materialIds : undefined,
          image: supplierUploadedFile,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const errText = data?.detail ? JSON.stringify(data.detail) : data?.message || `Failed to add product (${res.status})`;
          failures.push(`${type.interiorType}: ${errText}`);
          continue;
        }
        successCount += 1;
      }

      if (successCount === 0) {
        throw new Error(failures[0] || 'Failed to save product');
      }

      setAddMaterialSuccess(true);
      if (failures.length > 0) {
        setAddMaterialError(
          `Saved ${successCount}/${selectedTypes.length} interior type(s). Failed: ${failures.join(' | ')}`
        );
      } else {
        setName('');
        setDescription('');
        setSelectedInteriorTypes([]);
        setMaterialsTextureColor('');
        setMaterialIds([]);
      }
    } catch (err) {
      setAddMaterialError(err?.message ?? 'Failed to save product');
    } finally {
      setAddMaterialLoading(false);
    }
  }, [name, description, selectedInteriorTypes, supplierUploadedFile, materialIds]);

  const handleAddMaterialFromPopup = useCallback(async () => {
    if (!addMaterialPopupFile) {
      setAddMaterialPopupError('Please upload an image.');
      return;
    }
    const materialName = (addMaterialPopupType || '').trim();
    if (!materialName) {
      setAddMaterialPopupError('Please enter a material type name.');
      return;
    }
    setAddMaterialPopupError(null);
    setAddMaterialPopupLoading(true);
    try {
      const res = await createMaterial({
        material_type: materialName,
        material_image: addMaterialPopupFile,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errText = data?.detail ? JSON.stringify(data.detail) : data?.message || `Failed to add material (${res.status})`;
        throw new Error(errText);
      }
      const id = data.id ?? data.materialId;
      if (id != null) {
        const suffix = `${materialName} (id: ${id})`;
        setMaterialsTextureColor((prev) => (prev ? `${prev}, ${suffix}` : suffix));
        setMaterialIds((prev) => [...prev, id]);
      }
      setShowAddMaterialPopup(false);
      setAddMaterialPopupFile(null);
      setAddMaterialPopupType('');
    } catch (err) {
      setAddMaterialPopupError(err?.message ?? 'Failed to add material');
    } finally {
      setAddMaterialPopupLoading(false);
    }
  }, [addMaterialPopupFile, addMaterialPopupType]);

  return (
    <div className="w-full">
      <h1 className="text-4xl font-bold text-gray-800 mb-8">Upload Product</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: image preview */}
        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 min-h-[320px] flex items-center justify-center">
          {supplierUploadedImage ? (
            <img
              src={supplierUploadedImage}
              alt="Uploaded product"
              className="max-h-[400px] max-w-full w-auto object-contain rounded"
            />
          ) : (
            <p className="text-gray-500 text-sm">No image. Upload an image on the Supplier page and click Let&apos;s Begin.</p>
          )}
        </div>

        {/* Right: Name, Description, Interior Type, Materials/Texture/Color */}
        <div className="border border-gray-200 rounded-lg p-6 bg-white flex flex-col space-y-4">
          <div>
            <label htmlFor="product-name" className="block text-sm font-medium text-gray-700 mb-2">
              Name
            </label>
            <input
              id="product-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Product name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label htmlFor="product-description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              id="product-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Product description"
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label htmlFor="interior-type" className="block text-sm font-medium text-gray-700 mb-2">
              Interior Type
            </label>
            <div className="relative">
            <input
              id="interior-type"
              type="text"
              value={materialTypeQuery}
              onChange={(e) => setMaterialTypeQuery(e.target.value)}
              onFocus={() => setMaterialTypeDropdownOpen(true)}
              onBlur={() => setTimeout(() => setMaterialTypeDropdownOpen(false), 150)}
              placeholder="Search or type..."
              autoComplete="off"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {materialTypeDropdownOpen && interiorTypesLoading ? (
              <div className="absolute left-0 right-0 top-full mt-1 py-2 px-3 bg-white border border-gray-200 rounded-lg shadow-lg z-20 text-sm text-gray-500">
                Loading types...
              </div>
            ) : materialTypeDropdownOpen && interiorTypesList.length > 0 ? (
              <ul
                className="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1"
                role="listbox"
              >
                {filteredTypes.map((t) => (
                  <li
                    key={t.id}
                    role="option"
                    className="px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 cursor-pointer truncate"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => addSelected(t)}
                  >
                    {t.interiorType}
                  </li>
                ))}
                {filteredTypes.length === 0 && (
                  <li className="px-3 py-2 text-sm text-gray-500">No matches</li>
                )}
              </ul>
            ) : null}
          </div>

          {/* Selected interior types below (label + id, remove) */}
          {selectedInteriorTypes.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Selected</p>
              <ul className="space-y-2">
                {selectedInteriorTypes.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-2 py-2 px-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <span className="text-sm text-gray-800 truncate">{s.interiorType}</span>
                    <span className="text-xs text-gray-500 shrink-0">ID: {s.id}</span>
                    <button
                      type="button"
                      onClick={() => removeSelected(s.id)}
                      className="shrink-0 p-1 rounded text-gray-500 hover:text-red-600 hover:bg-gray-200"
                      aria-label={`Remove ${s.interiorType}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <label htmlFor="materials-texture-color" className="block text-sm font-medium text-gray-700">
                Materials / Texture / Color
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowAddMaterialPopup(true);
                  setAddMaterialPopupError(null);
                  setAddMaterialPopupFile(null);
                  setAddMaterialPopupType('');
                  setTimeout(() => addMaterialPopupInputRef.current?.focus(), 100);
                }}
                className="text-sm font-medium text-gray-800 bg-gray-200 hover:bg-gray-300 px-3 py-1.5 rounded-lg transition-colors"
              >
                Add New
              </button>
            </div>
            <input
              id="materials-texture-color"
              type="text"
              value={materialsTextureColor}
              onChange={(e) => setMaterialsTextureColor(e.target.value)}
              placeholder="e.g. Oak, matte, navy — or add via Add New"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Add Material popup (for Materials/Texture/Color) */}
          {showAddMaterialPopup && (
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-material-popup-title"
            >
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
                <h2 id="add-material-popup-title" className="text-lg font-semibold text-gray-900">
                  Add material
                </h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setAddMaterialPopupFile(file ?? null);
                      setAddMaterialPopupError(null);
                    }}
                    className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-200 file:text-gray-800"
                  />
                  {addMaterialPopupFile && (
                    <p className="mt-1 text-xs text-gray-500">{addMaterialPopupFile.name}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="popup-material-type" className="block text-sm font-medium text-gray-700 mb-1">
                    Material Type
                  </label>
                  <input
                    ref={addMaterialPopupInputRef}
                    id="popup-material-type"
                    type="text"
                    value={addMaterialPopupType}
                    onChange={(e) => {
                      setAddMaterialPopupType(e.target.value);
                      setAddMaterialPopupError(null);
                    }}
                    placeholder="Type material name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                {addMaterialPopupError && (
                  <p className="text-sm text-red-600">{addMaterialPopupError}</p>
                )}
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMaterialPopup(false);
                      setAddMaterialPopupError(null);
                      setAddMaterialPopupFile(null);
                      setAddMaterialPopupType('');
                    }}
                    className="flex-1 py-2.5 px-4 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddMaterialFromPopup}
                    disabled={addMaterialPopupLoading}
                    className="flex-1 py-2.5 px-4 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-700 disabled:opacity-50"
                  >
                    {addMaterialPopupLoading ? 'Saving…' : 'Add Material'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-2">
            <button
              type="button"
              onClick={handleAddProduct}
              disabled={addMaterialLoading || !name.trim() || selectedInteriorTypes.length === 0 || !supplierUploadedFile}
              className="w-full py-3 px-4 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              {addMaterialLoading ? 'Saving…' : 'Add Product'}
            </button>
            {addMaterialError && (
              <p className="text-sm text-red-600">{addMaterialError}</p>
            )}
            {addMaterialSuccess && (
              <p className="text-sm text-green-600">Product added successfully.</p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <button
          type="button"
          onClick={() => setCurrentView('Supplier')}
          className="text-gray-600 hover:text-gray-800 text-sm"
        >
          ← Back to Supplier
        </button>
      </div>
    </div>
  );
};

export default UploadMaterial;
